import React, { useState } from 'react';
import { seguradoraService } from '../../services/entitiesServices';
import { AuthService } from '../../services/authService';
import type { Seguradora } from '../../types/entities';

interface SeguradoraFormModalProps {
  seguradora?: Seguradora;
  onClose: () => void;
  onSuccess?: (seguradora: Seguradora) => void;
}

export const SeguradoraFormModal: React.FC<SeguradoraFormModalProps> = ({ seguradora, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: seguradora?.nome || '',
    taxaComissao: seguradora?.taxaComissao || 0,
    custoAdministrativo: seguradora?.custoAdministrativo || 0,
    // include empresaId from authenticated user as default so backend can validate access
    empresaId: seguradora?.empresaId || AuthService.getEmpresaId() || undefined,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      // Ensure empresaId is present in payload (backend validateEmpresaAccess expects it sometimes)
      const payload = { ...formData } as any;
      if (!payload.empresaId) {
        payload.empresaId = AuthService.getEmpresaId();
      }

      if (seguradora) {
        // Editar
        result = await seguradoraService.update(seguradora.id, payload);
      } else {
        // Criar
        result = await seguradoraService.create(payload);
      }

      onSuccess?.(result.data);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar seguradora:', err);
      setError(err instanceof Error ? err.message : 'Erro ao salvar seguradora');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  {seguradora ? 'Editar Seguradora' : 'Nova Seguradora'}
                </h3>
                {seguradora?.nome && (
                  <p className="text-xs text-gray-500 mt-0.5">{seguradora.nome}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200">
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="nome" className="block text-xs font-medium text-gray-700 mb-1">
                Nome da Seguradora *
              </label>
              <input
                type="text"
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Digite o nome da seguradora"
                required
              />
            </div>

            {/* codigo was removed from schema - not used */}

            <div>
              <label htmlFor="taxaComissao" className="block text-xs font-medium text-gray-700 mb-1">
                Taxa de Comissão (%)
              </label>
              <input
                type="number"
                id="taxaComissao"
                value={formData.taxaComissao}
                onChange={(e) => setFormData({ ...formData, taxaComissao: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label htmlFor="custoAdministrativo" className="block text-xs font-medium text-gray-700 mb-1">
                Custo Administrativo
              </label>
              <input
                type="number"
                id="custoAdministrativo"
                value={formData.custoAdministrativo}
                onChange={(e) => setFormData({ ...formData, custoAdministrativo: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Salvando...' : (seguradora ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};